import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { usersAdminApi, type AdminUserView } from '../../api/users';
import { AsyncBoundary } from '../../ui/AsyncBoundary';
import { Badge } from '../../ui/Badge';
import { Button } from '../../ui/Button';
import { ErrorNotice } from '../../ui/ErrorNotice';
import { Modal } from '../../ui/Modal';
import { inputClass } from '../../ui/Field';
import { describeMember, filterSlackMembers } from './slack-members';

/**
 * Slack-Konto von Hand verknüpfen.
 *
 * Der Ausweg, wenn der Mailabgleich nicht greift. `SLACK_EMAIL_DOMAINS` deckt
 * einen Domainwechsel ab und UPNs auf `onmicrosoft.com` — aber nicht
 * unterschiedliche lokale Teile: Steht in Entra `t.schwarz@beans.de` und in
 * Slack `tobias.schwarz@rocketbeans.de`, hilft kein Domaintausch. Ohne diesen
 * Dialog bliebe nur ein INSERT von Hand.
 */
export function SlackLinkDialog({ user, onClose }: { user: AdminUserView; onClose: () => void }) {
  const queryClient = useQueryClient();
  const [term, setTerm] = useState('');

  const members = useQuery({
    queryKey: ['slack-members'],
    queryFn: usersAdminApi.slackMembers,
    // Die Liste kommt am Stück und ändert sich selten; einmal je Sitzung
    // genügt. Der Endpunkt holt bei Slack mehrere Seiten und liegt in einem
    // knappen Tier.
    staleTime: 5 * 60_000,
  });

  const link = useMutation({
    mutationFn: (slackUserId: string) => usersAdminApi.setSlackLink(user.id, slackUserId),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['admin-users'] });
      onClose();
    },
  });

  return (
    <Modal
      title={`Slack-Konto für ${user.displayName}`}
      description="Nötig, wenn die Mailadresse in Slack von der in Entra abweicht — dann findet der automatische Abgleich das Konto nicht."
      onClose={onClose}
    >
      <div className="space-y-3">
        {user.slackLink && !user.slackLink.revokedAt && (
          <p className="rounded-md border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900">
            Bereits verknüpft mit <span className="font-mono">{user.slackLink.slackUserId}</span>.
            Eine neue Auswahl ersetzt diese Verknüpfung.
          </p>
        )}

        <input
          value={term}
          onChange={(event) => setTerm(event.target.value)}
          placeholder="Name, @handle oder Mailadresse"
          className={inputClass}
          autoFocus
        />

        {link.isError && <ErrorNotice error={link.error} />}

        <AsyncBoundary
          query={members}
          empty="Keine Slack-Mitglieder abrufbar. Ist Slack eingerichtet und der Bot installiert?"
        >
          {(all) => {
            const matches = filterSlackMembers(all, term);

            if (matches.length === 0) {
              return (
                <p className="rounded-md border border-slate-200 p-6 text-center text-sm text-slate-500">
                  Kein Slack-Konto passt zu „{term}".
                </p>
              );
            }

            return (
              <ul className="max-h-80 divide-y divide-slate-100 overflow-y-auto rounded-md border border-slate-200">
                {matches.map((member) => (
                  <li key={member.id}>
                    <button
                      type="button"
                      onClick={() => link.mutate(member.id)}
                      disabled={link.isPending}
                      className="flex w-full cursor-pointer items-center justify-between gap-3 px-3 py-2.5 text-left hover:bg-slate-50 disabled:opacity-50"
                    >
                      <span className="min-w-0">
                        <span className="block truncate font-medium">{member.displayName}</span>
                        <span className="block truncate text-xs text-slate-500">
                          {describeMember(member) || member.id}
                        </span>
                      </span>
                      {member.isGuest && <Badge tone="amber">Gast</Badge>}
                    </button>
                  </li>
                ))}
              </ul>
            );
          }}
        </AsyncBoundary>

        <div className="flex justify-end pt-1">
          <Button onClick={onClose}>Abbrechen</Button>
        </div>
      </div>
    </Modal>
  );
}
